import { observer, useLocalObservable } from 'mobx-react';
import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
  selectedProjectTabPageSize,
  SELECTED_PROJECT_TABS,
  encodeProjectsParams
} from './constants';
import Pagination from '../../../components/navigation/Pagination';
import useHistory from '../../../hooks/useHistory';
import { paths } from '../../../utils/constants/routes';
import ProfilerFeedbackSearchResultsTabEntry from './ProfilerFeedbackSearchResultsTabEntry';
import IconButton, {
  ICON_BUTTON_ICONS,
  ICON_BUTTON_TYPES
} from '../../../components/buttons/IconButton';
import { useStore } from '../../../store/store';
import {
  coerceDisplayedProfilerSort,
  filterRegisterStatusSortOptions,
  hasEbitMarginData
} from '../../search/SearchResults/constants/constants';
import useProfilerViewControls from '../../../hooks/useProfilerViewControls';
import { hasKnownRegisterStatus } from '../../../utils/companyStatus';
import {
  isProfilerListVisible,
  PROFILER_VIEWS,
  PROFILER_VIEWS_LIST,
  hasSavedProfilerHeatmapGroupingToggleByProjectView,
  sortProfilerResults
} from '../../ProfilerProjects/profilerProjectViewConstants';
import ProfilerHeatmapSection from '../../../components/profiler/HeatmapSection/ProfilerHeatmapSection';
import ProfilerBarChart from '../../../components/profiler/ProfilerBarChart/ProfilerBarChart';
import {
  getEffectiveChartSort,
  isSortOptionVisibleInChart
} from '../../../components/profiler/ProfilerBarChart/chartSortDefaults';
import { UI_OPTIONS } from '../../../utils/constants/uiOptions';
import { UI_OPTION_KEYS } from '../../../utils/constants/uiOptionKeys';
import MobileSheet from '../../../components/common/MobileSheet';
import ResultsMobileActionBar from '../../../components/common/ResultsMobileActionBar';
import { isInsideResultsMobileBar } from '../../../utils/resultsMobileBar';
import { DATA_SOURCE, dataSourceClass } from '../../../utils/constants/dataSources';

const getHeatmapGroupingProperty = (searchCategory) => {
  if (searchCategory === UI_OPTIONS[UI_OPTION_KEYS.PROFILER_SEARCH_CATEGORY]?.['Industries']) {
    return '_industryCode';
  }
  if (searchCategory === UI_OPTIONS[UI_OPTION_KEYS.PROFILER_SEARCH_CATEGORY]?.Characteristics) {
    return 'scoreLabel';
  }
  return null;
};

const ProfilerFeedbackSearchResultsTabView = observer(
  ({
    project = {},
    results = [],
    feedbacks = [],
    forTab = SELECTED_PROJECT_TABS.RESULTS,
    params = {},
    displayDetails = () => {},
    onChangeFeedback,
    fullResultsCount,
    isFromAgentView = false,
    mobilePrimaryAction = null,
    // Project-level overflow for the mobile bar's More sheet (data sources).
    mobileMoreActions = [],
    // The parent's desktop toolbar mount above the tabs, and the controls it
    // splices around this view's View / Sort (Send feedback before, data sources after).
    toolbarMountNode = null,
    toolbarFeedbackSlot = null,
    toolbarTrailingSlot = null,
    // TD-2970 — project-level secondary bar actions from the parent (Download),
    // appended after this view's own View / Sort.
    mobileSecondaryActions = []
  }) => {
    const { authStore, profilerAccessStore, utilsStore } = useStore();
    const { navigate } = useHistory();
    const controls = useProfilerViewControls({
      searchId: project?.searchId,
      searchCategory: project?.searchCategory,
      // TD-2890 — the result set decides. Neither tier model was safe here: the viewer-only
      // one hides EBIT from a reviewer whose data has it, and the owner-inherited one offers
      // a sort the BE served no values for (measured on DEV: a Lite reviewer of a Full
      // requester's project gets 0/140 rows with a margin).
      hasEbitData: hasEbitMarginData(results)
    });
    // Same data gate as the search and project views: no known register status in the set
    // means the register status sorts would do nothing, so they are not offered.
    const hasRegisterStatusData = hasKnownRegisterStatus(results);
    const profilerSortOptions = filterRegisterStatusSortOptions(
      controls.profilerSortOptions,
      hasRegisterStatusData
    );
    const groupingResetCallbackRef = useRef(() => {});
    const scrollTimeoutRef = useRef(null);

    const state = useLocalObservable(() => ({
      forTab,
      setForTab: (value = '') => (state.forTab = value),
      get filteredResults() {
        return state.forTab === SELECTED_PROJECT_TABS.RESULTS
          ? results.filter((r) => !r.isExcluded)
          : results.filter((r) => r.isExcluded);
      },
      get layout() {
        return utilsStore.windowWidth < 1201 ? 'mobile' : 'desktop';
      }
    }));

    useEffect(() => {
      state.setForTab(forTab);
    }, [state, forTab]);

    const { profilerView, setProfilerView } = controls;

    // Force list view on very narrow screens (chart unsupported)
    useEffect(() => {
      if (utilsStore.windowWidth < 600 && profilerView === PROFILER_VIEWS.CHART) {
        setProfilerView(PROFILER_VIEWS.LIST);
      }
    }, [utilsStore.windowWidth, profilerView, setProfilerView]);

    // Default-on grouping when there's no saved preference and grouping is meaningful
    useEffect(() => {
      if (hasSavedProfilerHeatmapGroupingToggleByProjectView(project?.searchId)) {
        return;
      }
      const groupingProp = getHeatmapGroupingProperty(project?.searchCategory);
      if (!groupingProp) return;
      const filtered = results.filter((r) => !r.isExcluded);
      const distinctGroups = new Set(filtered.map((r) => r[groupingProp])).size;
      if (!controls.isProfilerHeatmapGroupingToggled && distinctGroups >= 2) {
        controls.toggleProfilerHeatmapGrouping();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Click-outside handlers for the four menus.
    // Depend on the specific stable callbacks (each useCallback in the hook),
    // not the whole `controls` object — that would re-attach listeners every render.
    const {
      isSortToggled,
      toggleSort,
      isProfilerViewMenuToggled,
      toggleProfilerViewMenu,
      isProfilerHeatmapTileSizeMenuToggled,
      toggleProfilerHeatmapTileSizeMenu,
      isProfilerHeatmapColorCodingMenuToggled,
      toggleProfilerHeatmapColorCodingMenu
    } = controls;

    useEffect(() => {
      const handler = (event) => {
        if (
          event.target.closest('.sort-menu-wrap') ||
          event.target.closest('#sort-btn-team') ||
          isInsideResultsMobileBar(event.target)
        ) {
          return;
        }
        if (isSortToggled) toggleSort();
      };
      document.addEventListener('click', handler);
      return () => document.removeEventListener('click', handler);
    }, [isSortToggled, toggleSort]);

    useEffect(() => {
      const handler = (event) => {
        if (
          event.target.closest('.view-menu-wrap') ||
          event.target.closest('#list-change-btn-team') ||
          isInsideResultsMobileBar(event.target)
        ) {
          return;
        }
        if (isProfilerViewMenuToggled) toggleProfilerViewMenu();
      };
      document.addEventListener('click', handler);
      return () => document.removeEventListener('click', handler);
    }, [isProfilerViewMenuToggled, toggleProfilerViewMenu]);

    useEffect(() => {
      const handler = (event) => {
        if (
          event.target.closest('.heatmap-control-menu-wrap.tile-size') ||
          event.target.closest('#profiler-tile-size-metric')
        ) {
          return;
        }
        if (isProfilerHeatmapTileSizeMenuToggled) toggleProfilerHeatmapTileSizeMenu();
      };
      document.addEventListener('click', handler);
      return () => document.removeEventListener('click', handler);
    }, [isProfilerHeatmapTileSizeMenuToggled, toggleProfilerHeatmapTileSizeMenu]);

    useEffect(() => {
      const handler = (event) => {
        if (
          event.target.closest('.heatmap-control-menu-wrap.color-coding') ||
          event.target.closest('#profiler-color-coding-metric')
        ) {
          return;
        }
        if (isProfilerHeatmapColorCodingMenuToggled) toggleProfilerHeatmapColorCodingMenu();
      };
      document.addEventListener('click', handler);
      return () => document.removeEventListener('click', handler);
    }, [isProfilerHeatmapColorCodingMenuToggled, toggleProfilerHeatmapColorCodingMenu]);

    const groupingProp = getHeatmapGroupingProperty(project?.searchCategory);
    const distinctGroups = groupingProp
      ? new Set(state.filteredResults.map((r) => r[groupingProp])).size
      : 0;
    const disableProfilerHeatmapGroup = !groupingProp || distinctGroups < 2;
    const profilerHeatmapGroup =
      !controls.isProfilerHeatmapGroupingToggled || disableProfilerHeatmapGroup
        ? null
        : groupingProp;

    const sortedResults = sortProfilerResults(state.filteredResults, controls.selectedSort);
    const showPagination = sortedResults.length > selectedProjectTabPageSize;
    const pages = Math.ceil(sortedResults.length / selectedProjectTabPageSize) || 1;
    const page = (params.selectedProjectTabPage || 1) - 1;
    const isHeatmap = controls.profilerView === PROFILER_VIEWS.HEATMAP;
    const isChart = controls.profilerView === PROFILER_VIEWS.CHART;
    // TD-2733 — was `!isHeatmap && !isChart`. Defining the list as the *absence* of the
    // other views is what made List and Heatmap mutually exclusive; the heatmap now sits
    // above the list, so both views show it and only Chart replaces it.
    const isListVisible = isProfilerListVisible(controls.profilerView);
    // TD-2719 — the sort the results are really ordered by: the Chart view falls back to
    // EBIT-margin ↓ while nothing is chosen. Display only — applying and persisting a
    // sort stays on `controls.selectedSort`, so List keeps relevance order.
    const hasEbitData = hasEbitMarginData(results);
    // TD-2890 — in Chart view with no margins the fallback ranks by CAGR 3Y instead of
    // EBIT margin; outside it, a stale EBIT selection shows as relevance.
    const effectiveSort = coerceDisplayedProfilerSort(
      getEffectiveChartSort(controls.selectedSort, isChart, hasEbitData),
      { hasEbitData, hasRegisterStatusData }
    );

    const getEntryFeedbacks = (entryId) =>
      feedbacks.filter((feedback) => feedback.resultId === entryId);

    const onToggleGrouping = () => {
      const nextValue = !controls.isProfilerHeatmapGroupingToggled;
      controls.toggleProfilerHeatmapGrouping();
      groupingResetCallbackRef.current(nextValue);
    };

    // The bag <ProfilerHeatmapSection/> forwards to its controls. Built fresh on every
    // render deliberately: `observer()` wraps the section in React.memo, so a reference
    // stabilised with useMemo and then mutated would be skipped and the dropdowns would
    // freeze. The two overrides are members this surface computes locally rather than
    // holding on a store.
    const heatmapControls = {
      ...controls,
      toggleProfilerHeatmapGrouping: onToggleGrouping,
      disableProfilerHeatmapGroup
    };

    useEffect(() => () => clearTimeout(scrollTimeoutRef.current), []);

    const scrollToResultsTop = () => {
      clearTimeout(scrollTimeoutRef.current);
      scrollTimeoutRef.current = setTimeout(() => {
        const el = document.getElementById('project-view-scroll-starting-point');
        const header = document.querySelector('header');
        if (el) {
          const headerHeight = header?.clientHeight || 0;
          const desktopHeaderOffset = state.layout === 'desktop' ? 16 : 0;
          window.scrollTo({
            top: headerHeight ? el.offsetTop - headerHeight + desktopHeaderOffset : el.offsetTop,
            behavior: 'smooth'
          });
        }
      }, 100);
    };

    const onSelectSortOption = (value) => {
      controls.selectSortOption(value);
      controls.toggleSort();
      navigate(
        (isFromAgentView ? paths.TEAM : paths.PROFILER_TEAM) +
          '?params=' +
          encodeProjectsParams({ ...params, selectedProjectTabPage: 1 })
      );
      scrollToResultsTop();
    };

    // TD-2770 — View/Sort option lists extracted so one body serves both the desktop
    // anchored dropdown (default wrapClassName) and the mobile bottom-sheet
    // (`mobile-sheet-options`), mirroring the Projects results toolbar.
    const renderTeamViewMenu = (wrapClassName = 'view-menu-wrap') => (
      <div className={wrapClassName}>
        {PROFILER_VIEWS_LIST.slice()
          .filter((view) =>
            utilsStore.windowWidth < 600 ? view.value !== PROFILER_VIEWS.CHART : true
          )
          .map((view) => (
            <IconButton
              key={view.id}
              hoverType={ICON_BUTTON_TYPES.BLUE}
              withBorder={false}
              innerText={view.name}
              onClick={() => {
                controls.setProfilerView(view.value);
                controls.toggleProfilerViewMenu();
              }}
              disabled={controls.profilerView === view.value || !!view.disabled}
              filled={controls.profilerView === view.value}
              {...(view.icon ? { iconOnRight: true, icon: view.icon } : {})}
              tooltipText={view.tooltipText || ''}
            />
          ))}
      </div>
    );

    const renderTeamSortMenu = (wrapClassName = 'sort-menu-wrap') => (
      <div className={wrapClassName}>
        {profilerSortOptions
          .filter((option) => !isChart || isSortOptionVisibleInChart(option, controls.selectedSort))
          .map((option) => (
            <IconButton
              key={option.value}
              hoverType={ICON_BUTTON_TYPES.BLUE}
              withBorder={false}
              innerText={option.shortName || option.name}
              onClick={() => onSelectSortOption(option.value)}
              disabled={effectiveSort === option.value}
              filled={effectiveSort === option.value}
              {...(option.icon ? { iconOnRight: true, icon: option.icon } : {})}
            />
          ))}
      </div>
    );

    // TD-2770 — the DESKTOP View/Sort toolbar. Mobile no longer reuses these nodes:
    // the bottom bar runs in descriptor mode and renders its own real <button>s from
    // the same handlers, so the page-scoped toolbar styling never has to survive the
    // bar's portal and the controls are keyboard-reachable. The MobileSheets below
    // back the bar.
    // One right-aligned row with the parent's slots around View / Sort, as on
    // saved projects (TD-2575). Each menu sits in its own `.pv-toolbar-anchor`, so it
    // opens under its own button however many controls the row carries.
    const renderTeamToolbar = () => (
      <div className="team-feedback-toolbar-row">
        {toolbarFeedbackSlot}
        <span className="pv-toolbar-anchor">
          <IconButton
            id="list-change-btn-team"
            icon={PROFILER_VIEWS_LIST.find((view) => view.value === controls.profilerView)?.icon}
            type={ICON_BUTTON_TYPES.BLUE}
            tooltipText={
              controls.isProfilerViewMenuToggled || controls.isSortToggled ? '' : 'Switch view'
            }
            onClick={controls.toggleProfilerViewMenu}
            filled={controls.isProfilerViewMenuToggled}
          />
          {controls.isProfilerViewMenuToggled && renderTeamViewMenu()}
        </span>
        <span className="pv-toolbar-anchor">
          <IconButton
            id="sort-btn-team"
            icon={
              effectiveSort === 'default'
                ? ICON_BUTTON_ICONS.OCTI_LIST_UNORDERED
                : ICON_BUTTON_ICONS.OCTI_LIST_ORDERED
            }
            type={ICON_BUTTON_TYPES.BLUE}
            tooltipText={(() => {
              if (controls.isProfilerViewMenuToggled || controls.isSortToggled) return '';
              if (sortedResults.length) {
                if (effectiveSort === 'default') return 'Sort';
                return profilerSortOptions.find((o) => o.value === effectiveSort)?.name || 'Sort';
              }
              return 'Sort is unavailable at this moment';
            })()}
            onClick={controls.toggleSort}
            filled={controls.isSortToggled || effectiveSort !== 'default'}
          />
          {controls.isSortToggled && renderTeamSortMenu()}
        </span>
        {toolbarTrailingSlot}
      </div>
    );

    return (
      <>
        {/* The desktop toolbar renders above the tabs, in the parent's mount
          (portals keep the React tree, so it stays driven by this view's controls). It
          sits outside the empty-results branch below, so Send feedback stays reachable on
          an empty tab. Portals only once the mount exists (desktop). */}
        {state.layout === 'desktop' &&
          toolbarMountNode &&
          createPortal(renderTeamToolbar(), toolbarMountNode)}
        <div className="table-responsive">
          <MobileSheet
            isOpen={state.layout === 'mobile' && controls.isProfilerViewMenuToggled}
            onClose={() => controls.toggleProfilerViewMenu()}
            title="View"
          >
            {renderTeamViewMenu('mobile-sheet-options')}
          </MobileSheet>
          <MobileSheet
            isOpen={state.layout === 'mobile' && controls.isSortToggled}
            onClose={() => controls.toggleSort()}
            title="Sort by"
          >
            {renderTeamSortMenu('mobile-sheet-options')}
          </MobileSheet>

          {sortedResults.length === 0 ? (
            <div className="no-data">No items for this tab</div>
          ) : (
            <>
              {isHeatmap && (
                <ProfilerHeatmapSection
                  controls={heatmapControls}
                  results={sortedResults}
                  searchId={project?.searchId}
                  searchCategory={project?.searchCategory}
                  groupProperty={profilerHeatmapGroup}
                  layout={state.layout}
                  isCollapsed={controls.isProfilerHeatmapCollapsed}
                  onToggleCollapse={controls.toggleProfilerHeatmapCollapsed}
                  hasResults={sortedResults.length > 0}
                  // Results and Excluded are the same component with a different set, so the
                  // treemap is rebuilt rather than re-laid-out on a tab switch.
                  heatmapKey={state.forTab}
                  showCagr4Y={
                    controls.profilerHeatmapColorCoding ===
                    UI_OPTIONS[UI_OPTION_KEYS.PROFILER_HEATMAP_COLOR_CODING]?.['CAGR 4Y (%)']
                  }
                  displayDetails={displayDetails}
                  setToggleProfilerHeatmapGroupingCallback={(fn) => {
                    groupingResetCallbackRef.current = fn || (() => {});
                  }}
                />
              )}

              {/* TD-2733 — the column labels sit directly above the rows rather than in the
                sticky header, so the heatmap can stack above them. Bespoke rather than the
                shared <ProfilerColumnHeaderRow/>: this surface has no column model and no
                per-column filters, and its grid comes from team-feedback.less, which an
                inline grid template would fight.
                The labels carry the same data-source classes as on saved projects,
                so the data-sources toggle colours them. */}
              {isListVisible && state.layout === 'desktop' && (
                <div className="cols at-project-results profiler-search-cols detached">
                  <div className="col name">
                    <span className={dataSourceClass(DATA_SOURCE.REGISTRY)}>Company name</span>
                  </div>
                  <div className="col headquarter">
                    <span className={dataSourceClass(DATA_SOURCE.REGISTRY)}>Location</span>
                  </div>
                  <div className={`col industry ${dataSourceClass(DATA_SOURCE.WEBSITE)}`}>
                    Industry
                  </div>
                  <div className={`col value-chain ${dataSourceClass(DATA_SOURCE.WEBSITE)}`}>
                    Value chain
                  </div>
                  <div className={`col products ${dataSourceClass(DATA_SOURCE.WEBSITE)}`}>
                    Products
                  </div>
                  <div className="col revenue">
                    <span className={dataSourceClass(DATA_SOURCE.REGISTRY)}>Sales (€M)</span>
                  </div>
                  <div className="col employees">
                    <span className={dataSourceClass(DATA_SOURCE.REGISTRY)}>Employees</span>
                  </div>
                  <div className="col feedback">Feedback</div>
                </div>
              )}

              {isListVisible && (
                <>
                  {sortedResults
                    .slice(
                      ((params.selectedProjectTabPage || 1) - 1) * selectedProjectTabPageSize,
                      (params.selectedProjectTabPage || 1) * selectedProjectTabPageSize
                    )
                    .map((entry, index) => (
                      <ProfilerFeedbackSearchResultsTabEntry
                        key={entry.id}
                        entry={entry}
                        project={project}
                        feedbacks={getEntryFeedbacks(entry.id)}
                        displayDetails={displayDetails}
                        onChangeFeedback={onChangeFeedback}
                        params={params}
                        index={index}
                      />
                    ))}

                  {profilerAccessStore.isProfilerDemo &&
                    (!!authStore.companyUsers.find((u) => u.id === project.ownerId)
                      ?.isCognitoProfilerDemoLite ||
                      !!authStore.companyUsers.find((u) => u.id === project.ownerId)
                        ?.isCognitoProfilerDemoFull) &&
                    fullResultsCount > selectedProjectTabPageSize && (
                      <div className="profiler-demo-limited-results-row">
                        <div className="icon-wrap">
                          <img src={ICON_BUTTON_ICONS.EXCLAMATAION_CIRCLE} />
                        </div>
                        <div className="content">
                          <div className="title">Limited results shown in demo mode</div>
                          <div className="text">
                            You&apos;re viewing {selectedProjectTabPageSize} of {fullResultsCount}{' '}
                            results. Contact{' '}
                            <a href="mailto:support@transact.digital">support@transact.digital</a>{' '}
                            for a subscription to see the full list.
                          </div>
                        </div>
                      </div>
                    )}

                  <Pagination
                    show={showPagination}
                    onPageChange={(selected) => {
                      navigate(
                        (isFromAgentView ? paths.TEAM : paths.PROFILER_TEAM) +
                          '?params=' +
                          encodeProjectsParams({
                            ...params,
                            selectedProjectTabPage: selected + 1
                          })
                      );
                      scrollToResultsTop();
                    }}
                    page={page}
                    pages={pages}
                  />
                </>
              )}

              {isChart && (
                <ProfilerBarChart
                  companies={sortedResults}
                  onCompanyClick={displayDetails}
                  chartId={project?.searchId}
                  project={project}
                  sort={controls.selectedSort}
                  showEbit={hasEbitData}
                />
              )}
            </>
          )}
        </div>

        {/* TD-2770 — mobile bottom bar, rendered as a SIBLING of `.table-responsive`,
            never inside it. Its in-flow spacer must stay out of the heatmap's scroll
            container: ProfilerHeatmap runs a ResizeObserver that calls setDimensions
            on every size change, so a spacer inside the container perturbs its width
            (scrollbar toggling) and drives an endless resize -> relayout loop that
            freezes the mobile heatmap view. Outside, the spacer only adds page height.
            The View/Sort buttons open the sheets above; the primary CTA is
            Send-feedback, handed down as a descriptor by the parent. */}
        {state.layout === 'mobile' && (
          <ResultsMobileActionBar
            actions={[
              {
                key: 'view',
                label: 'View',
                icon: PROFILER_VIEWS_LIST.find((view) => view.value === controls.profilerView)
                  ?.icon,
                onClick: controls.toggleProfilerViewMenu,
                active: controls.isProfilerViewMenuToggled
              },
              {
                key: 'sort',
                label: 'Sort',
                icon:
                  effectiveSort === 'default'
                    ? ICON_BUTTON_ICONS.OCTI_LIST_UNORDERED
                    : ICON_BUTTON_ICONS.OCTI_LIST_ORDERED,
                onClick: controls.toggleSort,
                active: controls.isSortToggled || effectiveSort !== 'default'
              },
              ...mobileSecondaryActions
            ]}
            primary={mobilePrimaryAction}
            moreActions={mobileMoreActions}
            ariaLabel="Team feedback actions"
            breakpoint="to-1200"
          />
        )}
      </>
    );
  }
);

export default ProfilerFeedbackSearchResultsTabView;
